/*
 * Quotation（御見積書）固有スクリプト
 * 2026-08-31：固定ダミーデータを?quote=IDで切り替えるだけの実装から、
 * 実際の商品ページ（?handle=商品ハンドル）またはカート（?source=cart）から
 * その場で自動発行する仕組みに作り直した。
 * 数量に応じた割引は適用しない（ユーザー判断、単価×数量のみ）。
 *
 * 送料：
 * - 送料が計算できるのはカートからの見積り（source=cart）のみ。Shopify標準の
 *   /cart/shipping_rates.json（実際の配送設定・実カートに基づく本物の送料）を使う。
 *   カートの中身自体は書き換えない（数量欄で編集しても実カートには反映されない。
 *   実カートを操作すると副作用が出るためユーザー判断で見送り）。そのため数量編集時は
 *   送料を再取得せず、地域変更時のみ再取得する。
 * - 商品ページからの見積り（handle=xxx）は実カートを介さない1点だけの仮の見積りのため、
 *   送料は計算・表示しない（概算すら出さない。配送先地域によって金額が変わるため、
 *   誤解を招く数字を見積書に載せないというユーザー判断）。かわりに送料は含まれない旨と、
 *   送料・配送についてのページへの案内を表示する。
 * - カートモードのみ、地域選択と「送料を見積書に含める」チェックボックスを表示する。
 *   チェックを外すと送料行が非表示になり、合計にも含まれない（法人のお客様が送料別で
 *   見積書を発行したい場合などに使う）。
 *
 * 複数ページ対応（2026-09-01）：
 * - カートの点数が多く1ページに収まらない場合、ヘッダー（ロゴ・宛名・見積書番号等）と
 *   表の見出し行を各ページに繰り返し、合計・備考・フッターは最終ページのみに出す。
 *   最終ページ以外はフッターの代わりに「次頁へ続く」の表記をページ最下部に出す。
 * - ページの割り付けはヘッダー・表の見出し・各行はDOM実測（実際の高さ）で行うが、
 *   フッターだけはFOOTER_RESERVE_PX（固定値）を使う。実測すると、送料合計行の
 *   表示/非表示切り替えタイミングやflex:1子要素特有の計測クセの影響を受けて
 *   割り付けが不安定になっていたため（詳細はFOOTER_RESERVE_PXの説明を参照）。
 * - PAGE_HEIGHT_PXはA4用紙（@page { margin: 0 }）の実高さから.doc-pageの
 *   上下パディングを引いた、1ページに入る実コンテンツ高さの目安（安全マージン込み）。
 * - 単品モード（items.length===1固定）は複数ページになることがないため、この処理は
 *   実質カートモードのみで働く。数量編集はページ内の見た目の高さを変えないため、
 *   初回描画時に一度だけページ割りを行えばよい（再割り付けは不要）。
 */
(function (SANBY) {
  const { escapeHtml } = SANBY.utils;
  const yen = (n) => Math.round(n).toLocaleString("ja-JP");

  // 都道府県ごとに実APIへ渡す都道府県名（Shopify表記）・郵便番号と、送料無料までの
  // 進捗表示に使うしきい値（確定値。北海道4,950円・沖縄4,450円、それ以外は3,950円）を持つ。
  // 実際の送料自体はfetchRealShippingFeeが返す本物の値を使うので、ここに金額は持たない。
  const REGIONS = {
    hokkaido: { name: "北海道", province: "Hokkaido", zip: "060-0001", freeThreshold: 4950 },
    aomori: { name: "青森県", province: "Aomori", zip: "030-0801", freeThreshold: 3950 },
    iwate: { name: "岩手県", province: "Iwate", zip: "020-0001", freeThreshold: 3950 },
    miyagi: { name: "宮城県", province: "Miyagi", zip: "980-0001", freeThreshold: 3950 },
    akita: { name: "秋田県", province: "Akita", zip: "010-0001", freeThreshold: 3950 },
    yamagata: { name: "山形県", province: "Yamagata", zip: "990-0031", freeThreshold: 3950 },
    fukushima: { name: "福島県", province: "Fukushima", zip: "960-8001", freeThreshold: 3950 },
    ibaraki: { name: "茨城県", province: "Ibaraki", zip: "310-0011", freeThreshold: 3950 },
    tochigi: { name: "栃木県", province: "Tochigi", zip: "320-0001", freeThreshold: 3950 },
    gunma: { name: "群馬県", province: "Gunma", zip: "371-0022", freeThreshold: 3950 },
    saitama: { name: "埼玉県", province: "Saitama", zip: "330-0061", freeThreshold: 3950 },
    chiba: { name: "千葉県", province: "Chiba", zip: "260-0013", freeThreshold: 3950 },
    tokyo: { name: "東京都", province: "Tokyo", zip: "100-0001", freeThreshold: 3950 },
    kanagawa: { name: "神奈川県", province: "Kanagawa", zip: "231-0001", freeThreshold: 3950 },
    niigata: { name: "新潟県", province: "Niigata", zip: "950-0901", freeThreshold: 3950 },
    toyama: { name: "富山県", province: "Toyama", zip: "930-0001", freeThreshold: 3950 },
    ishikawa: { name: "石川県", province: "Ishikawa", zip: "920-0001", freeThreshold: 3950 },
    fukui: { name: "福井県", province: "Fukui", zip: "910-0001", freeThreshold: 3950 },
    yamanashi: { name: "山梨県", province: "Yamanashi", zip: "400-0031", freeThreshold: 3950 },
    nagano: { name: "長野県", province: "Nagano", zip: "380-0846", freeThreshold: 3950 },
    gifu: { name: "岐阜県", province: "Gifu", zip: "500-8801", freeThreshold: 3950 },
    shizuoka: { name: "静岡県", province: "Shizuoka", zip: "420-0853", freeThreshold: 3950 },
    aichi: { name: "愛知県", province: "Aichi", zip: "460-0008", freeThreshold: 3950 },
    mie: { name: "三重県", province: "Mie", zip: "514-0001", freeThreshold: 3950 },
    shiga: { name: "滋賀県", province: "Shiga", zip: "520-0001", freeThreshold: 3950 },
    kyoto: { name: "京都府", province: "Kyoto", zip: "600-8001", freeThreshold: 3950 },
    osaka: { name: "大阪府", province: "Osaka", zip: "530-0001", freeThreshold: 3950 },
    hyogo: { name: "兵庫県", province: "Hyogo", zip: "650-0001", freeThreshold: 3950 },
    nara: { name: "奈良県", province: "Nara", zip: "630-8113", freeThreshold: 3950 },
    wakayama: { name: "和歌山県", province: "Wakayama", zip: "640-8001", freeThreshold: 3950 },
    tottori: { name: "鳥取県", province: "Tottori", zip: "680-0011", freeThreshold: 3950 },
    shimane: { name: "島根県", province: "Shimane", zip: "690-0001", freeThreshold: 3950 },
    okayama: { name: "岡山県", province: "Okayama", zip: "700-0901", freeThreshold: 3950 },
    hiroshima: { name: "広島県", province: "Hiroshima", zip: "730-0011", freeThreshold: 3950 },
    yamaguchi: { name: "山口県", province: "Yamaguchi", zip: "753-0001", freeThreshold: 3950 },
    tokushima: { name: "徳島県", province: "Tokushima", zip: "770-0831", freeThreshold: 3950 },
    kagawa: { name: "香川県", province: "Kagawa", zip: "760-0001", freeThreshold: 3950 },
    ehime: { name: "愛媛県", province: "Ehime", zip: "790-0001", freeThreshold: 3950 },
    kochi: { name: "高知県", province: "Kochi", zip: "780-0850", freeThreshold: 3950 },
    fukuoka: { name: "福岡県", province: "Fukuoka", zip: "810-0001", freeThreshold: 3950 },
    saga: { name: "佐賀県", province: "Saga", zip: "840-0801", freeThreshold: 3950 },
    nagasaki: { name: "長崎県", province: "Nagasaki", zip: "850-0031", freeThreshold: 3950 },
    kumamoto: { name: "熊本県", province: "Kumamoto", zip: "860-0001", freeThreshold: 3950 },
    oita: { name: "大分県", province: "Oita", zip: "870-0001", freeThreshold: 3950 },
    miyazaki: { name: "宮崎県", province: "Miyazaki", zip: "880-0001", freeThreshold: 3950 },
    kagoshima: { name: "鹿児島県", province: "Kagoshima", zip: "890-0001", freeThreshold: 3950 },
    okinawa: { name: "沖縄県", province: "Okinawa", zip: "900-0001", freeThreshold: 4450 },
  };

  // A4（96dpi換算 約1122.9px）から.doc-pageの上下パディング（56px×2）を引いた実測目安に、
  // ブラウザ差・端数のための安全マージンを見込んだ値。
  const PAGE_HEIGHT_PX = 980;
  // 画面プレビューの1ページカードは.doc-page-frameのaspect-ratio(210/297)と
  // width:820pxから決まる高さ（約1158.86px）。分割が要るかどうかの判定はこちらと比べる
  // （PAGE_HEIGHT_PXは印刷時の安全マージン込みの予算で、これより小さいため、混同すると
  // 1ページに収まる分量でも誤って分割してしまう。2026-09-01のバグ修正）。
  const SINGLE_PAGE_HEIGHT_PX = 820 * (297 / 210);
  // フッター（合計box＋備考＋フッター注記）の予算。実測（DOM高さ）ではなく固定値に
  // している。実測にすると、送料合計行の表示/非表示切り替えタイミング（recalcTotalsが
  // paginateIfNeededの後に呼ばれるため、計測時点ではまだ送料行がhiddenのまま）や、
  // flex:1子要素特有の計測クセの影響を受け、不安定な割り付けになっていた。
  // どのページの予算からも一律に同じ値を差し引く（最終ページ以外は使わない分がそのまま
  // 余白になるが、それは「次頁へ続く」の表記で埋める＝表示の見た目の話であって、
  // ページ割り付けの計算そのものとは別の話）。ページごとに予算を出し分けようとすると、
  // 「全部1ページ扱いになる件数」と「行数だけで自然に2ページに分かれる件数」の境目で
  // 予算が食い違い、件数を1つ増やしただけで前のページの件数が変わる不具合が起きたため、
  // 単純な一律差し引きに戻した（2026-09-01）。
  const FOOTER_RESERVE_PX = 110;

  let items = [];
  let isCartMode = false;
  let shippingFee = 0;
  let quoteMeta = null; // { quoteNo, issueDate, validUntil }

  function getParams() {
    return new URLSearchParams(window.location.search);
  }

  function currentRegion() {
    const select = document.querySelector("[data-input-region]");
    return REGIONS[select && select.value] || REGIONS.tokyo;
  }

  function includeShipping() {
    const checkbox = document.querySelector("[data-input-include-shipping]");
    return isCartMode && checkbox && checkbox.checked;
  }

  function fetchProductItem(handle) {
    return fetch(`/products/${handle}.js`)
      .then((res) => (res.ok ? res.json() : null))
      .then((p) => (p ? [{ name: p.title, price: p.price / 100, qty: 1 }] : []))
      .catch(() => []);
  }

  function fetchCartItems() {
    return SANBY.cartApi.getCart().then((cart) =>
      cart.items.map((item) => ({
        name: item.variant_title && item.variant_title !== "Default Title" ? `${item.product_title}（${item.variant_title}）` : item.product_title,
        price: item.price / 100,
        qty: item.quantity,
      }))
    );
  }

  // カートモード：実際の配送設定に基づく本物の送料を取得する。実カートの中身に対してのみ
  // 計算できる仕組みのため、この見積りフォーム上で数量を編集しても再取得はされない。
  function fetchRealShippingFee(region) {
    const params = new URLSearchParams({
      "shipping_address[zip]": region.zip,
      "shipping_address[country]": "Japan",
      "shipping_address[province]": region.province,
    });
    return fetch(`/cart/shipping_rates.json?${params.toString()}`)
      .then((res) => (res.ok ? res.json() : { shipping_rates: [] }))
      .then((data) => (data.shipping_rates && data.shipping_rates.length > 0 ? Number(data.shipping_rates[0].price) : null))
      .catch(() => null);
  }

  // カートモードかつ「送料を含める」がオンのときだけ、実送料を再取得する。
  // それ以外（単品モード／チェックが外れている）は送料0のまま合計を再計算する。
  function updateShippingFee() {
    if (!includeShipping() || items.length === 0) {
      shippingFee = 0;
      recalcTotals();
      return;
    }

    shippingFee = undefined; // 取得中は「計算中」表示にする
    recalcTotals();
    const region = currentRegion();
    fetchRealShippingFee(region).then((fee) => {
      // 数量や重量で実際の送料は変わり得るため、取得できない場合に概算へフォールバックはせず、
      // 正直に「取得できなかった」ことを示す（null）。合計・消費税の計算からも除外する
      // （recalcTotals参照。誤った金額を見積書に載せないためのユーザー判断、2026-09-01）。
      shippingFee = fee;
      recalcTotals();
    });
  }

  function genQuoteNo(now) {
    const y = now.getFullYear();
    const m = String(now.getMonth() + 1).padStart(2, "0");
    const d = String(now.getDate()).padStart(2, "0");
    const rand = Math.floor(1000 + Math.random() * 9000);
    return `Q-${y}${m}${d}-${rand}`;
  }

  function formatDate(d) {
    return `${d.getFullYear()}年${d.getMonth() + 1}月${d.getDate()}日`;
  }

  function rowHtml(it, i) {
    return `
      <div class="doc-table-row">
        <div class="doc-table-cell col-name">${escapeHtml(it.name)}</div>
        <div class="doc-table-cell col-qty">${
          isCartMode ? it.qty : `<input type="number" min="1" class="doc-qty-input" value="${it.qty}" data-qty-index="${i}">`
        }</div>
        <div class="doc-table-cell col-price">¥${yen(it.price)}</div>
        <div class="doc-table-cell col-total" data-line-total-index="${i}">¥${yen(it.price * it.qty)}</div>
      </div>`;
  }

  function renderItemRows() {
    const itemsEl = document.querySelector("[data-items]");
    const emptyEl = document.querySelector("[data-empty]");
    if (items.length === 0) {
      itemsEl.innerHTML = "";
      emptyEl.hidden = false;
      return;
    }
    emptyEl.hidden = true;
    // カートモードは数量をこの見積書上で編集不可にする（実カートの中身とズレると、
    // 実カートに基づいて取得した送料の判定が実態と合わなくなるため）。
    itemsEl.innerHTML = items.map((it, i) => rowHtml(it, i)).join("");
  }

  // 御見積金額の大見出しや押印は1ページ目だけに出すのが一般的なので、2ページ目以降は
  // ロゴ・タイトル・見積書番号だけの簡易ヘッダーに差し替える（宛先・金額・印影は出さない）。
  function buildContinuationHeaderHtml(logoImgHtml) {
    return `
      <div class="doc-head doc-head-continued">
        <div class="doc-logo">${logoImgHtml}</div>
        <div class="doc-title-block">
          <div class="doc-title">御見積書</div>
          <div class="doc-issue-date">見積書番号：<span data-quote-no></span></div>
        </div>
      </div>
      <div class="doc-divider"></div>`;
  }

  // 画面に出さずに高さだけ測るための一時要素。ページ本体（.doc-page）と同じ横幅で
  // レンダリングして正しい折返し・余白込みの高さを得る。
  function measureDetachedHeight(html, referenceEl) {
    const probe = document.createElement("div");
    probe.style.position = "absolute";
    probe.style.visibility = "hidden";
    probe.style.pointerEvents = "none";
    probe.style.width = getComputedStyle(referenceEl).width;
    probe.innerHTML = html;
    document.body.appendChild(probe);
    const height = probe.getBoundingClientRect().height;
    document.body.removeChild(probe);
    return height;
  }

  // items.length行が1ページに収まらない場合、ヘッダー・表見出しの実測高さをもとに
  // 複数ページへ分割し直す。1ページに収まる場合は何もしない（従来通りの単一ページ構造の
  // まま）。フッターの高さはFOOTER_RESERVE_PX（固定値）を使うため、ここでは測らない。
  function paginateIfNeeded() {
    if (items.length === 0) return;
    const frame = document.querySelector(".doc-page-frame");
    if (!frame) return;
    // .doc-page-frameはaspect-ratio指定のため、getBoundingClientRect().heightは常に
    // SINGLE_PAGE_HEIGHT_PXに固定されており、中身があふれても増えない（overflow:visibleで
    // 見た目には中身がはみ出すだけ）。あふれているかどうかはscrollHeight（実際のコンテンツの
    // 高さ）で判定する必要がある（2026-09-01、getBoundingClientRect().heightで判定していたため
    // 分割が実質発動しないバグがあり修正）。
    if (frame.scrollHeight <= SINGLE_PAGE_HEIGHT_PX) return;

    const page = frame.querySelector(".doc-page");
    const headerEl = page.querySelector("[data-page-header]");
    const tableEl = page.querySelector(".doc-table");
    const tableHeadEl = page.querySelector(".doc-table-row.is-head");
    const rowEls = Array.from(page.querySelectorAll("[data-items] .doc-table-row"));
    const footerEl = page.querySelector("[data-page-footer]");
    if (rowEls.length !== items.length) return;

    const logoImgHtml = headerEl.querySelector(".doc-logo-img").outerHTML;
    const continuationHeaderHtml = buildContinuationHeaderHtml(logoImgHtml);

    const headerHeight = headerEl.getBoundingClientRect().height;
    const continuationHeaderHeight = measureDetachedHeight(continuationHeaderHtml, page);
    const tableMarginTop = parseFloat(getComputedStyle(tableEl).marginTop) || 0;
    const tableHeadHeight = tableHeadEl.getBoundingClientRect().height;
    const rowHeights = rowEls.map((el) => el.getBoundingClientRect().height);
    const headBlockHeightFirst = headerHeight + tableMarginTop + tableHeadHeight;
    const headBlockHeightRest = continuationHeaderHeight + tableMarginTop + tableHeadHeight;

    // フッターのうち「本書は...お問い合わせください。」の罫線＋文言（.doc-footer-row）は
    // 毎ページ共通で出す。合計box・備考（.doc-totals-row等）は最終ページのみ。
    // footerEl自体のクローンから.doc-footer-rowを取り除いたものを最終ページ用フッターとし、
    // .doc-footer-rowは別テンプレートとして毎ページ分に使う。
    const footerRowEl = footerEl.querySelector(".doc-footer-row");
    const footerWithoutRowEl = footerEl.cloneNode(true);
    const footerRowInClone = footerWithoutRowEl.querySelector(".doc-footer-row");
    if (footerRowInClone) footerRowInClone.remove();

    const templates = {
      header: headerEl.outerHTML,
      continuationHeader: continuationHeaderHtml,
      tableHead: tableHeadEl.outerHTML,
      footer: footerWithoutRowEl.outerHTML,
      footerRow: footerRowEl.outerHTML,
      continuedNote: '<div class="doc-continued-note">次頁へ続く</div>',
    };

    const pages = packPages(rowHeights, headBlockHeightFirst, headBlockHeightRest);
    if (pages.length <= 1) return;
    renderPaginated(pages, templates);
  }

  // 行の高さ配列を、ヘッダー分＋FOOTER_RESERVE_PXを毎ページ差し引いた予算で先頭から
  // 目一杯詰める。1ページ目と2ページ目以降でヘッダーの高さが異なる（続きページは簡易
  // ヘッダー）ため、ページごとに使う予算を切り替える。それだけの単純な前詰め。
  function packPages(rowHeights, headBlockHeightFirst, headBlockHeightRest) {
    const n = rowHeights.length;
    const budget = PAGE_HEIGHT_PX - FOOTER_RESERVE_PX;
    const pages = [];
    let idx = 0;
    while (idx < n) {
      const headHeight = pages.length === 0 ? headBlockHeightFirst : headBlockHeightRest;
      let used = headHeight;
      let count = 0;
      while (idx + count < n && used + rowHeights[idx + count] <= budget) {
        used += rowHeights[idx + count];
        count++;
      }
      if (count === 0) count = 1; // 1行だけでページ予算を超える極端なケースでも必ず前進させる
      pages.push({ startIndex: idx, count });
      idx += count;
    }

    pages.forEach((p, i) => (p.isLast = i === pages.length - 1));
    return pages;
  }

  function renderPaginated(pages, templates) {
    const scrollEl = document.querySelector(".doc-page-scroll");
    const totalPages = pages.length;
    scrollEl.innerHTML = pages
      .map((def, pageIndex) => {
        const headerHtml = pageIndex === 0 ? templates.header : templates.continuationHeader;
        const rowsHtml = Array.from({ length: def.count }, (_, k) => rowHtml(items[def.startIndex + k], def.startIndex + k)).join("");
        const tableHtml = `<div class="doc-table">${templates.tableHead}<div data-items>${rowsHtml}</div></div>`;
        const continuedNoteHtml = def.isLast ? "" : templates.continuedNote;
        // 合計box・備考は最終ページのみ。罫線＋「本書は...」の文言（footerRow）は毎ページ
        // 共通で出す（ページ番号はその下に続く）。
        const footerHtml = def.isLast ? templates.footer : "";
        // 最終ページはtemplates.footer（flex:1＋内部spacer）がfooterRowを最下部まで
        // 押し下げるので単体で置く。最終ページ以外はfooterRowだけが単独で置かれるため、
        // 同じ仕組み（flex:1＋spacer）を持つラッパーで自力で最下部まで押し下げる。
        const footerRowHtml = def.isLast
          ? templates.footerRow
          : `<div class="doc-footer-row-wrap"><div class="doc-spacer"></div>${templates.footerRow}</div>`;
        const pageNumberHtml = `<div class="doc-page-number">${pageIndex + 1} / ${totalPages}</div>`;
        const breakClass = pageIndex < totalPages - 1 ? " doc-page-break" : "";
        return `<div class="doc-page-frame${breakClass}"><section class="doc-page">${headerHtml}${tableHtml}${continuedNoteHtml}${footerHtml}${footerRowHtml}${pageNumberHtml}</section></div>`;
      })
      .join("");

    paintQuoteMeta();
    paintCustomerFields();
  }

  // 数量編集時の再計算：小計・税・合計はその場で反映するが、送料はここでは再取得しない
  // （updateShippingFee参照。実カートを操作しないための制約）。
  function recalcTotals() {
    items.forEach((it, i) => {
      const el = document.querySelector(`[data-line-total-index="${i}"]`);
      if (el) el.textContent = "¥" + yen(it.price * it.qty);
    });

    const subtotal = items.reduce((sum, it) => sum + it.price * it.qty, 0);
    const shippingIncluded = includeShipping();
    // 送料が数値で確定しているときだけ合計・消費税に含める。取得中／取得失敗は0円扱いで
    // 計算するが、送料欄自体には金額ではなく状態を表示して誤解を避ける。
    const shippingKnown = typeof shippingFee === "number";
    const effectiveShipping = shippingIncluded && shippingKnown ? shippingFee : 0;
    const taxable = subtotal + effectiveShipping;
    const tax = Math.round((taxable * 10) / 110);
    const grandTotal = taxable;

    document.querySelectorAll("[data-shipping-totals-line]").forEach((el) => (el.hidden = !shippingIncluded));
    document.querySelectorAll("[data-subtotal]").forEach((el) => (el.textContent = "¥" + yen(subtotal)));
    document.querySelectorAll("[data-shipping-fee]").forEach((el) => {
      el.textContent = shippingFee === undefined ? "計算中…" : shippingFee === null ? "取得できませんでした" : "¥" + yen(shippingFee);
    });
    document.querySelectorAll("[data-tax-amount]").forEach((el) => (el.textContent = "¥" + yen(tax)));
    document.querySelectorAll("[data-grand-total]").forEach((el) => (el.textContent = "¥" + yen(grandTotal)));
  }

  // ページが複数に分かれても[data-items]ごとに毎回束ね直さなくて済むよう、
  // 常に存在する.doc-page-scrollに委譲する（中身が入れ替わっても listener は残る）。
  function bindQtyInputs() {
    document.querySelector(".doc-page-scroll").addEventListener("input", (e) => {
      const input = e.target.closest("[data-qty-index]");
      if (!input) return;
      const i = Number(input.dataset.qtyIndex);
      const qty = Math.max(1, Math.floor(Number(input.value)) || 1);
      items[i].qty = qty;
      recalcTotals();
    });
  }

  function bindRegionSelect() {
    const select = document.querySelector("[data-input-region]");
    if (!select) return;
    select.addEventListener("change", updateShippingFee);
  }

  function bindIncludeShippingCheckbox() {
    const checkbox = document.querySelector("[data-input-include-shipping]");
    if (!checkbox) return;
    checkbox.addEventListener("change", updateShippingFee);
  }

  // 複数ページに分割されるとヘッダー（宛名等）がページごとに複製されるため、
  // 該当箇所は常にquerySelectorAllで全コピーへ反映する。
  function paintCustomerFields() {
    const companyInput = document.querySelector("[data-input-company]");
    const contactInput = document.querySelector("[data-input-contact]");
    const phoneInput = document.querySelector("[data-input-phone]");
    const addressInput = document.querySelector("[data-input-address]");

    document.querySelectorAll("[data-company]").forEach((el) => (el.textContent = companyInput.value));
    document.querySelectorAll("[data-company-suffix]").forEach((el) => (el.hidden = !companyInput.value.trim()));
    document.querySelectorAll("[data-contact]").forEach((el) => (el.textContent = contactInput.value));
    document.querySelectorAll("[data-contact-suffix]").forEach((el) => (el.hidden = !contactInput.value.trim()));
    document.querySelectorAll("[data-phone]").forEach((el) => (el.textContent = phoneInput.value));
    document.querySelectorAll("[data-address]").forEach((el) => (el.textContent = addressInput.value));
  }

  function bindCustomerFields() {
    ["[data-input-company]", "[data-input-contact]", "[data-input-phone]", "[data-input-address]"].forEach((selector) => {
      const input = document.querySelector(selector);
      if (input) input.addEventListener("input", paintCustomerFields);
    });
  }

  function paintQuoteMeta() {
    if (!quoteMeta) return;
    document.querySelectorAll("[data-quote-no]").forEach((el) => (el.textContent = quoteMeta.quoteNo));
    document.querySelectorAll("[data-issue-date]").forEach((el) => (el.textContent = quoteMeta.issueDate));
    document.querySelectorAll("[data-valid-until]").forEach((el) => (el.textContent = quoteMeta.validUntil));
  }

  // カートモードのみ、地域選択・送料チェックボックスを表示する。単品モードは
  // 地域選択の代わりに「送料は含まれない」の注記だけを表示する（地域選択・チェックボックスが
  // 出ている時点でカートは送料込みだと分かるため、カート向けの注記は不要というユーザー判断）。
  // 見積書本体の備考欄にある送料ページへの案内も同様に、単品モードのみ表示する。
  function toggleShippingUi() {
    const optionsEl = document.querySelector("[data-shipping-options]");
    const singleNoteEl = document.querySelector("[data-note-single-shipping]");
    const docShippingNoteEl = document.querySelector("[data-note-doc-shipping]");
    const qtyEditableNoteEl = document.querySelector("[data-note-qty-editable]");
    if (optionsEl) optionsEl.hidden = !isCartMode;
    if (singleNoteEl) singleNoteEl.hidden = isCartMode;
    if (docShippingNoteEl) docShippingNoteEl.hidden = isCartMode;
    // カートモードは数量が編集不可（renderItemRows参照）なので、この案内も出さない
    if (qtyEditableNoteEl) qtyEditableNoteEl.hidden = isCartMode;
  }

  function init() {
    const params = getParams();
    const handle = params.get("handle");
    isCartMode = params.get("source") === "cart";
    const fetcher = handle ? fetchProductItem(handle) : isCartMode ? fetchCartItems() : Promise.resolve([]);

    toggleShippingUi();

    fetcher.then((fetchedItems) => {
      items = fetchedItems;

      const now = new Date();
      const validUntil = new Date(now.getTime() + 30 * 24 * 60 * 60 * 1000);
      quoteMeta = { quoteNo: genQuoteNo(now), issueDate: formatDate(now), validUntil: formatDate(validUntil) };
      paintQuoteMeta();

      renderItemRows();
      paginateIfNeeded();
      updateShippingFee();
    });

    bindCustomerFields();
    bindQtyInputs();
    bindRegionSelect();
    bindIncludeShippingCheckbox();
  }

  document.addEventListener("DOMContentLoaded", () => {
    document.querySelector("[data-print]").addEventListener("click", () => window.print());
    init();
  });
})(window.SANBY);
